import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
    Upload, 
    FileText, 
    DollarSign, 
    Image as ImageIcon,
    X,
    Loader2,
    CheckCircle,
    Info
} from 'lucide-react';
import { PendingInvoicesAlert } from '../components/dashboard';

const API = `/api`;

export default function UploadInvoice() {
    const { getAuthHeaders, user } = useAuth();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        amount: '',
        category: ''
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [pendingInvoices, setPendingInvoices] = useState([]);
    const [nameError, setNameError] = useState('');
    const [submissionToken, setSubmissionToken] = useState(null);
    const [isTokenLoading, setIsTokenLoading] = useState(false);

    // Stripe fee calculation for preview
    const getStripeFeeInfo = () => {
        const cat = formData.category.toUpperCase();
        const gross = parseFloat(formData.amount);
        if (!gross || gross <= 0) return null;
        if (cat === 'S') {
            const fee = Math.round((gross * 0.029 + 0.30) * 100) / 100;
            const net = Math.round((gross - fee) * 100) / 100;
            return { type: 'Doméstico', rate: '2.9% + $0.30', gross, fee, net };
        }
        if (cat === 'SS') {
            const fee = Math.round((gross * 0.044 + 0.30) * 100) / 100;
            const net = Math.round((gross - fee) * 100) / 100;
            return { type: 'Internacional', rate: '4.4% + $0.30', gross, fee, net };
        }
        return null;
    };

    const stripeFeeInfo = getStripeFeeInfo();

    // Generate submission token when component loads
    const generateToken = async () => {
        setIsTokenLoading(true);
        try {
            const response = await axios.get(`${API}/invoices/generate-token`, {
                headers: getAuthHeaders()
            });
            setSubmissionToken(response.data.token);
        } catch (error) {
            console.error('Error generating submission token:', error);
        } finally {
            setIsTokenLoading(false);
        }
    };

    // Generate token on component mount
    useEffect(() => {
        generateToken();
    }, []);

    // Validate name - only letters, numbers and spaces allowed
    const validateName = (value) => {
        const invalidChars = /[^a-zA-Z0-9\s]/;
        
        if (invalidChars.test(value)) {
            return 'Solo letras, números y espacios.';
        }
        return '';
    };

    const handleNameChange = (e) => {
        const { value } = e.target;
        const error = validateName(value);
        setNameError(error);
        setFormData(prev => ({ ...prev, name: value }));
    };

    // Fetch pending invoices for the alert
    useEffect(() => {
        const fetchPendingInvoices = async () => {
            if (user?.role !== 'usuario') return;
            try {
                const response = await axios.get(`${API}/invoices?limit=100`, {
                    headers: getAuthHeaders()
                });
                const pending = response.data.filter(inv => !inv.confirmed);
                setPendingInvoices(pending);
            } catch (error) {
                console.error('Error fetching pending invoices:', error);
            }
        };
        fetchPendingInvoices();
    }, [user, getAuthHeaders]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileSelect = (file) => {
        if (file && file.type.startsWith('image/')) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onload = (e) => setPreview(e.target.result);
            reader.readAsDataURL(file);
        } else {
            toast.error('Por favor selecciona una imagen válida');
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        handleFileSelect(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const removeFile = () => {
        setSelectedFile(null);
        setPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.amount) {
            toast.error('Por favor completa el nombre y monto');
            return;
        }

        if (!formData.category) {
            toast.error('Por favor selecciona una categoría (banco)');
            return;
        }

        // Validate name before submitting
        const nameValidationError = validateName(formData.name);
        if (nameValidationError) {
            setNameError(nameValidationError);
            toast.error(nameValidationError);
            return;
        }

        // Check if we have a valid submission token
        if (!submissionToken) {
            toast.error('Error de sesión. Por favor recargue la página.');
            return;
        }

        setIsLoading(true);
        
        try {
            const submitData = new FormData();
            submitData.append('name', formData.name);
            submitData.append('description', formData.description);
            submitData.append('amount', parseFloat(formData.amount));
            submitData.append('category', formData.category);
            submitData.append('submission_token', submissionToken);
            if (selectedFile) {
                submitData.append('image', selectedFile);
            }

            await axios.post(`${API}/invoices`, submitData, {
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'multipart/form-data'
                }
            });

            toast.success('Factura subida correctamente');
            // Clear form and navigate
            setSubmissionToken(null); // Invalidate token after successful submission
            navigate('/dashboard/invoices');
        } catch (error) {
            console.error('Error uploading invoice:', error);
            
            // Handle duplicate submission error
            if (error.response?.status === 409) {
                toast.error('Este formulario ya ha sido enviado. Recargando página...');
                // Generate new token and reset form
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            } else {
                toast.error('Error al subir la factura');
                // Generate new token for retry
                generateToken();
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto animate-fade-in" data-testid="upload-invoice-page">
            {/* Pending Invoices Alert for Regular Users */}
            {user?.role === 'usuario' && (
                <PendingInvoicesAlert 
                    pendingCount={pendingInvoices.length}
                    pendingInvoices={pendingInvoices}
                />
            )}

            <div className="mb-8">
                <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
                    Subir Factura
                </h1>
                <p className="text-slate-500 mt-1">
                    Registra una nueva factura en el sistema
                </p>
            </div>

            <Card className="border-0 shadow-lg">
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5 text-sky-500" />
                        Nueva Factura
                    </CardTitle>
                    <CardDescription>
                        Completa los datos y adjunta una imagen de la factura
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Action Buttons - At top for mobile */}
                        <div className="flex gap-4 pb-2 border-b border-slate-100">
                            <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                onClick={() => navigate('/dashboard/invoices')}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                className="flex-1 bg-sky-500 hover:bg-sky-600"
                                disabled={isLoading || isTokenLoading || !submissionToken}
                                data-testid="submit-invoice-btn"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Subiendo...
                                    </>
                                ) : isTokenLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Preparando...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="mr-2 h-4 w-4" />
                                        Subir Factura
                                    </>
                                )}
                            </Button>
                        </div>

                        {/* Image Upload Zone */}
                        <div>
                            <Label className="text-slate-700 font-medium mb-2 block">
                                Imagen de la Factura
                            </Label>
                            <div
                                className={`upload-zone ${isDragging ? 'active' : ''}`}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onClick={() => fileInputRef.current?.click()}
                                data-testid="upload-zone"
                            >
                                {preview ? (
                                    <div className="relative inline-block">
                                        <img
                                            src={preview}
                                            alt="Preview"
                                            className="max-h-48 rounded-lg mx-auto"
                                        />
                                        <Button
                                            type="button"
                                            variant="destructive"
                                            size="sm"
                                            className="absolute -top-2 -right-2 w-8 h-8 rounded-full p-0"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeFile();
                                            }}
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-sky-50 flex items-center justify-center">
                                            <ImageIcon className="w-8 h-8 text-sky-500" />
                                        </div>
                                        <p className="text-slate-600 font-medium">
                                            Arrastra una imagen aquí
                                        </p>
                                        <p className="text-slate-400 text-sm mt-1">
                                            o haz clic para seleccionar
                                        </p>
                                    </>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleFileSelect(e.target.files[0])}
                                    data-testid="file-input"
                                />
                            </div>
                        </div>

                        {/* Name */}
                        <div className="space-y-2">
                            <Label htmlFor="name" className="text-slate-700 font-medium">
                                Nombre de la Factura
                            </Label>
                            <Input
                                id="name"
                                name="name"
                                placeholder="Ej: Juan Perez Garcia"
                                value={formData.name}
                                onChange={handleNameChange}
                                className={`h-11 border-slate-200 focus:border-sky-500 focus:ring-sky-500 ${
                                    nameError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                                }`}
                                required
                                data-testid="invoice-name-input"
                            />
                            {nameError && (
                                <p className="text-red-500 text-sm font-medium" data-testid="name-error">
                                    {nameError}
                                </p>
                            )}
                        </div>

                        {/* Description - Optional */}
                        <div className="space-y-2">
                            <Label htmlFor="description" className="text-slate-700 font-medium">
                                Descripción <span className="text-slate-400 font-normal">(opcional)</span>
                            </Label>
                            <Textarea
                                id="description"
                                name="description"
                                placeholder="Describe el contenido de la factura..."
                                value={formData.description}
                                onChange={handleInputChange}
                                className="min-h-24 border-slate-200 focus:border-sky-500 focus:ring-sky-500 resize-none"
                                data-testid="invoice-description-input"
                            />
                        </div>

                        {/* Amount */}
                        <div className="space-y-2">
                            <Label htmlFor="amount" className="text-slate-700 font-medium">
                                {stripeFeeInfo ? 'Monto Bruto (USD)' : 'Monto (USD)'}
                            </Label>
                            <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <Input
                                    id="amount"
                                    name="amount"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    value={formData.amount}
                                    onChange={handleInputChange}
                                    className="pl-10 h-11 border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                                    required
                                    data-testid="invoice-amount-input"
                                />
                            </div>
                            {/* Stripe Fee Breakdown */}
                            {stripeFeeInfo && (
                                <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50/60 p-3 space-y-1.5" data-testid="stripe-fee-breakdown">
                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-sky-700 mb-1">
                                        <Info className="w-3.5 h-3.5" />
                                        Desglose Stripe — {stripeFeeInfo.type} ({stripeFeeInfo.rate})
                                    </div>
                                    <div className="flex justify-between text-sm text-slate-600">
                                        <span>Importe del pago</span>
                                        <span>${stripeFeeInfo.gross.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm text-red-500">
                                        <span>Comisión Stripe</span>
                                        <span>- ${stripeFeeInfo.fee.toFixed(2)}</span>
                                    </div>
                                    <div className="border-t border-sky-200 pt-1.5 flex justify-between text-sm font-bold text-slate-800">
                                        <span>Neto (se guarda)</span>
                                        <span data-testid="stripe-net-amount">${stripeFeeInfo.net.toFixed(2)}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Category - Mandatory */}
                        <div className="space-y-2">
                            <Label htmlFor="category" className="text-slate-700 font-medium">
                                Categoría <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="category"
                                name="category"
                                placeholder="Ej: T, S, SS, B, L, A, E..."
                                value={formData.category}
                                onChange={(e) => {
                                    const val = e.target.value.toUpperCase().slice(0, 2);
                                    setFormData(prev => ({ ...prev, category: val }));
                                }}
                                className="h-11 border-slate-200 focus:border-sky-500 focus:ring-sky-500 uppercase"
                                maxLength={2}
                                required
                                data-testid="invoice-category-input"
                            />
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
